import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useArrangementSelections } from "hooks/useArrangementAPI";
import { TASK_NAMES } from "hooks/useCaseTasksAPI";
import { setTaskOptimism } from "helpers/taskOptimism";
import { useScrollPageToTop } from "context/PageScrollContext";

import { useArrangement } from "./ArrangementContext";
import StatementOfGoods from "./StatementOfGoods";
import SignDocumentsStep from "./SignDocumentsStep";
import PaymentSection from "./PaymentSection";
import ContinueOnBehalfPanel from "./ContinueOnBehalfPanel";
import QuoteDeliverySection from "./QuoteDeliverySection";
import CareSpecialistCard from "./CareSpecialistCard";
import ReviewActions from "./ReviewActions";
import { AT_NEED_CONFIRMATION_SUB_VIEWS } from "./screenModel";
import { useScreenTracking } from "./useScreenTracking";
import { STEP_ROUTES } from "./constants";
import type { ArrangementLifecycle } from "./types";

export default function CompletionStep() {
  const { state, isReadOnly, goToStep, isViewerPurchaser } = useArrangement();
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { search } = useLocation();

  // Lifecycle fields (selections_confirmed_by, document status, …) live on the
  // /selections response alongside the persisted choices. The provider already
  // subscribes to this key, so SWR dedupes — this is the same cached response.
  const { data: selectionsData, mutate: mutateSelections } =
    useArrangementSelections(caseId);
  const lifecycle =
    (selectionsData as { arrangement?: ArrangementLifecycle } | undefined)
      ?.arrangement ?? null;
  const selectionsConfirmedBy = lifecycle?.selections_confirmed_by ?? null;

  const [subViewIndex, setSubViewIndex] = useState(0);
  const scrollPageToTop = useScrollPageToTop();

  // Set once a non-purchaser viewer records the arrangement attestation (taken
  // at signing). One attestation covers both signing and payment, so this also
  // unlocks the payment form. Reset on reload, which simply re-prompts.
  const [hasAttested, setHasAttested] = useState(false);

  // Scroll to top on every sub-view change. Unlike the main flow steps (which
  // scroll via ArrangementNav), sign→pay advances by calling onComplete
  // directly, so reset scroll here to cover all sub-view transitions.
  useEffect(() => {
    scrollPageToTop();
  }, [subViewIndex, scrollPageToTop]);

  // Sign/pay require confirmed selections. If a viewer lands there without a
  // confirmation (deep link, or an edit cleared it), send them back to review.
  // Wait for the first /selections read so we don't bounce on cold load.
  useEffect(() => {
    if (!lifecycle) return;
    const current = AT_NEED_CONFIRMATION_SUB_VIEWS[subViewIndex];
    if ((current === "sign" || current === "pay") && !selectionsConfirmedBy) {
      setSubViewIndex(AT_NEED_CONFIRMATION_SUB_VIEWS.indexOf("review"));
    }
  }, [lifecycle, subViewIndex, selectionsConfirmedBy]);

  const screenDescriptor = isReadOnly
    ? null
    : state.isDeceased === false
      ? {
          screen_name: "confirmation-quote-delivery",
          step: "confirmation" as const,
        }
      : {
          screen_name: `confirmation-${AT_NEED_CONFIRMATION_SUB_VIEWS[subViewIndex]}`,
          step: "confirmation" as const,
        };
  const { markCompleted } = useScreenTracking(screenDescriptor);

  // CS read-only: show only the review with View buttons
  // Must check before isDeceased so read-only users never see editable components
  if (isReadOnly) {
    return (
      <div className="pb-24">
        <div className="mb-8 text-center">
          <h1 className="font-serif text-4xl italic text-evergreen">
            Arrangement Review
          </h1>
          <p className="mt-2 text-lg text-soil">
            Viewing the family&apos;s arrangement selections.
          </p>
        </div>
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <StatementOfGoods readOnly />
          <div className="space-y-6">
            <CareSpecialistCard />
            <ReviewsSocialProof />
          </div>
        </div>
      </div>
    );
  }

  // Pre-need path: show quote delivery only
  if (state.isDeceased === false) {
    return (
      <div className="grid gap-8 lg:grid-cols-2">
        <StatementOfGoods />
        <QuoteDeliverySection />
      </div>
    );
  }

  const subView = AT_NEED_CONFIRMATION_SUB_VIEWS[subViewIndex];

  const handleBack = () => {
    if (subViewIndex > 0) {
      setSubViewIndex(subViewIndex - 1);
    } else {
      goToStep(STEP_ROUTES.indexOf("services"));
    }
  };

  const handleContinue = () => {
    markCompleted();
    if (subViewIndex < AT_NEED_CONFIRMATION_SUB_VIEWS.length - 1) {
      setSubViewIndex(subViewIndex + 1);
    }
  };

  const handlePaymentComplete = () => {
    markCompleted();
    // Payment locks the arrangement server-side. Optimistically flip the cached
    // arrangement_status to "submitted" so the wizard locks prior steps
    // immediately (no stale-draft window where the family could edit and hit
    // silent 409s), then let Basecamp Hub own the next step.
    mutateSelections(
      (current?: { arrangement?: { status?: string } }) =>
        current
          ? {
              ...current,
              arrangement: { ...current.arrangement, status: "submitted" },
            }
          : current,
      { revalidate: true },
    );
    if (caseId) {
      setTaskOptimism(caseId, TASK_NAMES.completeArrangements);
      navigate(`/cases/${caseId}${search}`, { replace: true });
    }
  };

  // The purchaser can pay directly; any other viewer must first attest they're
  // acting on the purchaser's behalf, which also unlocks the payment form.
  const canEnterPayment = isViewerPurchaser || hasAttested;

  const subtitle = (() => {
    switch (subView) {
      case "review":
        return "Review your selections before finalizing.";
      case "sign":
        return "We're getting everything ready. This usually takes about one minute — please keep this window open.";
      case "pay":
        return "No charge today — we're just saving your card on file.";
    }
  })();

  return (
    <div className="pb-24">
      <div className="mb-8 text-center">
        {/* The sign screen renders its own per-state heading. */}
        {subView !== "sign" && (
          <>
            <h1 className="font-serif text-4xl italic text-evergreen">
              {subView === "review"
                ? "Review Your Arrangement"
                : "Save your card"}
            </h1>
            {subtitle && <p className="mt-2 text-lg text-soil">{subtitle}</p>}
          </>
        )}

        {/* Substep progress */}
        <div className="mx-auto mt-4 flex items-center justify-center gap-2">
          {AT_NEED_CONFIRMATION_SUB_VIEWS.map((v, idx) => (
            <div
              key={v}
              className={`h-1.5 w-10 rounded-full transition-colors ${
                idx <= subViewIndex ? "bg-sage" : "bg-oat"
              }`}
            />
          ))}
        </div>
      </div>

      {subView === "review" && (
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <StatementOfGoods />
          <div className="space-y-6">
            <CareSpecialistCard />
            <ReviewsSocialProof />
          </div>
        </div>
      )}
      {subView === "sign" && (
        <SignDocumentsStep
          onComplete={handleContinue}
          hasAttested={hasAttested}
          onAttested={() => setHasAttested(true)}
        />
      )}
      {subView === "pay" && (
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          {canEnterPayment ? (
            <PaymentSection onComplete={handlePaymentComplete} />
          ) : (
            <ContinueOnBehalfPanel onAttested={() => setHasAttested(true)} />
          )}
          <div className="space-y-6">
            <CareSpecialistCard />
            <ReviewsSocialProof />
          </div>
        </div>
      )}

      {subView === "review" && (
        <ReviewActions
          confirmedBy={selectionsConfirmedBy}
          onBack={handleBack}
          onContinue={handleContinue}
          revalidate={mutateSelections}
        />
      )}
    </div>
  );
}

function ReviewsSocialProof() {
  return (
    <div className="flex flex-col items-center gap-2 p-5">
      <img
        loading="lazy"
        alt="Five gold stars"
        src="/public/stars-reviews.svg"
        className="h-6"
      />
      <p className="font-bold text-evergreen">
        4.9 stars after 1,700+ reviews!
      </p>
      <div className="flex gap-4">
        <img
          loading="lazy"
          alt="Google logo"
          src="/public/google-logo.svg"
          className="h-8"
        />
        <img
          loading="lazy"
          alt="Trustpilot logo"
          src="/public/trustpilot-logo.svg"
          className="h-8"
        />
      </div>
    </div>
  );
}
